import React, { useState, useEffect, useRef } from "react";
import {
    Box,
    Typography,
    TextField,
    Table,
    TableBody,
    TableCell,
    TableHead,
    TableRow,
    Paper,
    Button,
} from "@mui/material";
import { useSettings } from "../context/SettingsContext";
import { Line } from "react-chartjs-2";
import {
    Chart as ChartJS,
    LineElement,
    CategoryScale,
    LinearScale,
    PointElement,
    Tooltip,
    Legend,
} from "chart.js";
import jsPDF from "jspdf";
import autoTable from "jspdf-autotable";
import { useParams, useNavigate } from "react-router-dom";

ChartJS.register(
    LineElement,
    CategoryScale,
    LinearScale,
    PointElement,
    Tooltip,
    Legend
);

export default function AthleteView() {
    const { settings } = useSettings();
    const { selectedCodex } = settings;

    const { noP } = useParams();
    const navigate = useNavigate();

    const [rows, setRows] = useState([]);
    const [selectedAthlete, setSelectedAthlete] = useState(null);

    // Sélection manuelle
    const [manualSelection, setManualSelection] = useState({});

    // Référence au graphique
    const chartRef = useRef(null);

    // Charger la liste de force + sélectionner l’athlète
    useEffect(() => {
        fetch("/listeforce")
            .then((res) => res.json())
            .then((data) => {
                setRows(data);

                if (noP) {
                    const ath = data.find((a) => String(a.noP) === String(noP));
                    if (ath) handleSelect(ath);
                }
            });
    }, [noP]);

    // ---------------------------------------------------------
    // Sélection athlète
    // ---------------------------------------------------------
    const handleSelect = (ath) => {
        const enrichedResults = (ath.results || []).map((r) => {
            const p = r.penaltyFinal ?? r.penalty;
            const penalty = p == null || p === "" ? null : parseFloat(p);
            return { ...r, penaltyFinal: penalty };
        });

        const validPenalties = enrichedResults
            .map((r) => r.penaltyFinal)
            .filter((p) => typeof p === "number" && !Number.isNaN(p));

        const mean =
            validPenalties.length > 0
                ? validPenalties.reduce((acc, val) => acc + val, 0) / validPenalties.length
                : null;

        const stdDev =
            validPenalties.length > 1
                ? Math.sqrt(
                    validPenalties
                        .map((p) => Math.pow(p - mean, 2))
                        .reduce((acc, val) => acc + val, 0) / validPenalties.length
                )
                : null;

        setSelectedAthlete({
            ...ath,
            results: enrichedResults,
            meanPenalty: mean,
            stdPenalty: stdDev,
        });

        setManualSelection({});
    };

    // ---------------------------------------------------------
    // Tableau des courses (backend only)
    // ---------------------------------------------------------
    const courseRows = selectedAthlete
        ? selectedAthlete.results
            .filter((r) => selectedCodex.includes(String(r.codex)))
            .map((r) => ({
                codex: r.codex,
                discipline: r.discipline,
                event: r.event,
                penalty: r.penaltyFinal ?? null,
                used: selectedAthlete.used?.some(
                    (u) => String(u.codex) === String(r.codex)
                ),

            }))
        : [];

    // ---------------------------------------------------------
    // Statistiques manuelles
    // ---------------------------------------------------------
    const manualPenalties = courseRows
        .filter((c) => manualSelection[c.codex])
        .map((c) => c.penalty)
        .filter((v) => v !== null && !isNaN(v));

    const manualMean =
        manualPenalties.length > 0
            ? manualPenalties.reduce((a, b) => a + b, 0) / manualPenalties.length
            : null;

    const manualStd =
        manualPenalties.length > 1
            ? Math.sqrt(
                manualPenalties
                    .map((v) => Math.pow(v - manualMean, 2))
                    .reduce((a, b) => a + b, 0) / manualPenalties.length
            )
            : null;

    // ---------------------------------------------------------
    // Export PDF avec graphique
    // ---------------------------------------------------------
    const exportPDF = () => {
        if (!selectedAthlete) return;

        const doc = new jsPDF();
        doc.setFontSize(16);
        doc.text(`Athlète : ${selectedAthlete.name}`, 20, 20);

        doc.setFontSize(12);
        doc.text(`Club : ${selectedAthlete.club}`, 20, 28);
        doc.text(`NoP : ${selectedAthlete.noP}`, 20, 36);

        doc.text(`Moyenne des pénalités : ${selectedAthlete.meanPenalty?.toFixed(2) ?? "—"}`, 20, 56);
        doc.text(`Écart-type : ${selectedAthlete.stdPenalty?.toFixed(2) ?? "—"}`, 20, 64);

        // Statistiques manuelles
        doc.text("Statistiques manuelles :", 20, 78);
        doc.text(`- Nombre de courses sélectionnées : ${manualPenalties.length}`, 20, 86);
        doc.text(`- Moyenne : ${manualMean ? manualMean.toFixed(2) : "—"}`, 20, 94);
        doc.text(`- Écart-type : ${manualStd ? manualStd.toFixed(2) : "—"}`, 20, 102);

        // Graphique
        const chart = chartRef.current;
        let tableStartY = 115;

        if (chart) {
            const chartCanvas = chart.canvas;
            const chartImg = chartCanvas.toDataURL("image/png", 1.0);
            doc.addImage(chartImg, "PNG", 20, 115, 170, 80);
            tableStartY = 200;
        }

        // Tableau
        const tableData = courseRows.map((r) => [
            r.codex,
            r.discipline,
            r.penalty ?? "—",

            manualSelection[r.codex] ? "✓" : "",
        ]);

        autoTable(doc, {
            startY: tableStartY,
            head: [["Codex", "Discipline", "Penalty", "Manuel"]],
            body: tableData,
        });

        doc.save(`${selectedAthlete.name}_stats.pdf`);
    };

    // ---------------------------------------------------------
    // Render
    // ---------------------------------------------------------
    if (!selectedAthlete) {
        return (
            <Box sx={{ padding: 3 }}>
                <Typography variant="h5">Athlète introuvable</Typography>
                <Button sx={{ mt: 2 }} onClick={() => navigate("/listeforce")}>
                    ← Retour
                </Button>
            </Box>
        );
    }

    return (
        <Box sx={{ padding: 3 }}>
            <Paper sx={{ padding: 2 }}>
                <Typography variant="h6">
                    🧑‍🎿 {selectedAthlete.name} – {selectedAthlete.club}
                </Typography>

                <Box sx={{ display: "flex", flexDirection: { xs: "column", md: "row" }, gap: 4, marginTop: 3 }}>
                    {/* Statistiques */}
                    <Box sx={{ flex: 1 }}>
                        <Typography variant="h6" gutterBottom>📊 Statistiques</Typography>

                                               <Typography>
                            Moyenne des pénalités : {selectedAthlete.meanPenalty?.toFixed(2) ?? "—"}
                        </Typography>

                        <Typography>
                            Écart-type : {selectedAthlete.stdPenalty?.toFixed(2) ?? "—"}
                        </Typography>

                        <Box sx={{ mt: 2 }}>
                            <Typography variant="h6">📊 Statistiques manuelles</Typography>

                            <Typography>
                                Nombre de courses sélectionnées : {manualPenalties.length}
                            </Typography>

                            <Typography>
                                Moyenne : {manualMean ? manualMean.toFixed(2) : "—"}
                            </Typography>

                            <Typography>
                                Écart‑type : {manualStd ? manualStd.toFixed(2) : "—"}
                            </Typography>
                        </Box>
                    </Box>

                    <Box sx={{ flex: 1, minWidth: 300 }}>
                        <Typography variant="h6" gutterBottom>📈 Évolution des pénalités</Typography>

                        <Box
                            sx={{
                                maxHeight: "33vh",
                                minHeight: "200px",
                                position: "relative",
                                padding: 1,
                            }}
                        >
                 <Line
                                ref={chartRef}
                                data={{
                                    labels: courseRows.map((r) => r.codex),
                                    datasets: [
                                        {
                                            label: "Pénalités par course",
                                            data: courseRows.map((r) => r.penalty),
                                            borderColor: "#1976d2",
                                            backgroundColor: "rgba(25, 118, 210, 0.3)",
                                            tension: 0.3,
                                            pointRadius: 5,
                                        },
                                    ],
                                }}
                                options={{
                                    responsive: true,
                                    maintainAspectRatio: false,
                                    scales: {
                                        y: { min: 0, max: 400 },
                                    },
                                }}
                            />
                        </Box>
                    </Box>
                </Box>


                    {/* Tableau */}
                <Table sx={{ marginTop: 3 }}>
                    <TableHead>
                        <TableRow>
                            <TableCell>Codex</TableCell>
                            <TableCell>Discipline</TableCell>
                            <TableCell>Penalty</TableCell>
                            <TableCell>Manuel</TableCell>
                        </TableRow>
                    </TableHead>
                    <TableBody>
                        {courseRows.map((row) => (
                            <TableRow key={row.codex}>
                                <TableCell>{row.codex}</TableCell>
                                <TableCell>{row.discipline}</TableCell>
                                <TableCell>{row.penalty ? row.penalty.toFixed(2) : "—"}</TableCell>


                                <TableCell>
                                    <input
                                        type="checkbox"
                                        checked={manualSelection[row.codex] || false}
                                        onChange={(e) =>
                                            setManualSelection((prev) => ({
                                                ...prev,
                                                [row.codex]: e.target.checked,
                                            }))
                                        }
                                    />
                                </TableCell>
                            </TableRow>
                        ))}
                    </TableBody>
                </Table>

                {/* Export PDF */}
                <Button
                    variant="contained"
                    color="primary"
                    sx={{ marginTop: 3, marginRight: 2 }}
                    onClick={exportPDF}
                >
                    Exporter PDF
                </Button>

                {/* Retour */}
                <Button
                    variant="outlined"
                    sx={{ marginTop: 3 }}
                    onClick={() => navigate("/listforce")}
                >
                    ← Retour à la liste
                </Button>
            </Paper>
        </Box>
    );
}
